import React from 'react';
import {
  Form,
  useSubmit,
  useNavigation,
  useLoaderData,
  useNavigate,
  useLocation,
  useOutlet,
  useActionData,
  useParams,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
  Await as RouterAwait,
  Link
} from 'react-router-dom';

import {
  MagnifyingGlassIcon,
  CircleStackIcon,
  ChevronDownIcon,
  ArrowUpTrayIcon,
  PencilSquareIcon,
  EyeIcon,
  Cog6ToothIcon,
  ShareIcon,
  UserGroupIcon,
  XMarkIcon,
  GlobeAltIcon,
  ClockIcon,
  FolderIcon,
  PlusIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import {
  DocumentIcon as DocumentIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  FolderIcon as FolderIconSolid,
  GlobeAltIcon as GlobeAltIconSolid
} from '@heroicons/react/24/solid';
import {
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
  Dialog,
  MountableDialog
} from '@oysterjs/ui/dialog';
import { PageTitle } from '@oysterjs/ui/page';
import { Input } from '@oysterjs/ui/input';
import { Button } from '@oysterjs/ui/button';
import { Select } from '@oysterjs/ui/select';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Description,
  ErrorMessage
} from '@oysterjs/ui/fieldset';
import {
  CreateKnowledgeBaseTextDocumentMutation,
  CreateKnowledgeBaseUrlDocumentDocument,
  GetKnowledgeBaseDocumentDocument,
  GetKnowledgeBaseDocumentQuery,
  GetKnowledgeBaseDocumentVersionHistoryDocument,
  GetKnowledgeBaseDocumentVersionHistoryQuery,
  GetFederatedChildAgenciesDocument,
  GetFederatedChildAgenciesQuery,
  GrantKnowledgeBaseAccessDocument,
  GrantKnowledgeBaseAccessMutation,
  CreateKnowledgeBaseFolderDocument,
  CreateKnowledgeBaseFolderMutation,
  DeleteKnowledgeBaseFolderDocument,
  DeleteKnowledgeBaseFolderMutation,
  DeleteKnowledgeBaseItemsDocument,
  DeleteKnowledgeBaseItemsMutation,
  RenameKnowledgeBaseFolderDocument,
  RenameKnowledgeBaseFolderMutation,
  MoveKnowledgeBaseFolderDocument,
  MoveKnowledgeBaseFolderMutation,
  MoveKnowledgeBaseItemsToFolderDocument,
  MoveKnowledgeBaseItemsToFolderMutation,
  MoveKnowledgeBaseDocumentsToFolderDocument,
  MoveKnowledgeBaseDocumentsToFolderMutation,
  UpdateKnowledgeBaseDocument as UpdateKnowledgeBaseMutationDocument,
  UpdateKnowledgeBaseMutation,
  UpdateKnowledgeBaseAccessDocument,
  UpdateKnowledgeBaseAccessMutation,
  RevokeKnowledgeBaseAccessDocument,
  RevokeKnowledgeBaseAccessMutation,
  ReplaceKnowledgeBaseDocumentDocument,
  ReplaceKnowledgeBaseDocumentMutation,
  DeleteKnowledgeBaseDocumentDocument,
  DeleteKnowledgeBaseDocumentMutation,
  SyncKnowledgeBaseUrlDocumentDocument,
  SyncKnowledgeBaseUrlDocumentMutation,
  UpdateKnowledgeBaseDocumentDocument,
  Action,
  IndexingStatus,
  KnowledgeBaseAccessRole,
  KnowledgeBaseDocumentType,
  KnowledgeBaseDetailFieldsFragment,
  KnowledgeBaseDocumentDetailFieldsFragment,
  KnowledgeBaseDocumentFieldsFragment,
  KnowledgeBaseDocumentVersionFieldsFragment,
  Resource,
  KnowledgeBaseFolderFieldsFragment,
  KnowledgeBaseAccessFieldsFragment,
  UpdateKnowledgeBaseDocumentInput,
  UpdateKnowledgeBaseDocumentMutation,
  UrlSourceType,
  UrlMonitorFrequency,
  CaraWorkspaceFileInput
} from '~/api/graphql';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { Spinner } from '@oysterjs/ui/spinner';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FileInput } from '~/components/file';
import { PermissionNotice, usePermissions } from '~/components/permissions';

/**
 * Folders and knowledge-base settings are KNOWLEDGE_BASE updates; documents
 * carry their own permissions. Both sit behind the per-base access list, so a
 * viewer needs the permission and an invitation.
 */
const MANAGE_KNOWLEDGE_BASE = [{ resource: Resource.KnowledgeBase, action: Action.Update }];
const CREATE_KB_DOCUMENT = [{ resource: Resource.KnowledgeBaseDocument, action: Action.Create }];
const UPLOAD_KB_DOCUMENT = [{ resource: Resource.KnowledgeBaseDocument, action: Action.Upload }];
const UPDATE_KB_DOCUMENT = [{ resource: Resource.KnowledgeBaseDocument, action: Action.Update }];
const DELETE_KB_DOCUMENT = [{ resource: Resource.KnowledgeBaseDocument, action: Action.Delete }];
const ANY_KB_WRITE = [
  ...MANAGE_KNOWLEDGE_BASE,
  ...CREATE_KB_DOCUMENT,
  ...UPLOAD_KB_DOCUMENT,
  ...UPDATE_KB_DOCUMENT,
  ...DELETE_KB_DOCUMENT
];
import { Badge } from '@oysterjs/ui/badge';
import { ApolloError } from '@apollo/client';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';
import { getApplicationError } from '@oysterjs/core/graphql/errors';
import {
  type CopilotActionError,
  copilotActionErrorFromApplicationError
} from '~/lib/copilot-action-error';
import { CopilotSubmitError } from '~/components/overage-limit-error';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { SearchFilterInput, type ActiveFilter } from '@oysterjs/ui/search-filter';
import { client } from '~/api/client';
import {
  createFilterDefinitions,
  applyFilters,
  KnowledgeBaseDocumentFilter
} from '~/api/data/knowledge/filters';
import {
  getKnowledgeBaseDocumentDisplayTitle,
  resolveKnowledgeBaseDocumentDisplayNameUpdate
} from '~/components/knowledge/document-display-name';
import { Table, TableCell, TableBody, TableHead, TableHeader, TableRow } from '@oysterjs/ui/table';
import { type ViewMode, ViewModeToggle } from '~/components/view-mode-toggle';
import { KnowledgeBaseUploadDialog } from '~/components/knowledge/knowledge-base-upload-dialog';
import { KnowledgeBaseIndexingRefresh } from '~/components/knowledge/knowledge-base-indexing-refresh';

const getFolderId = (folderId?: string | null) => {
  const trimmed = folderId?.trim();
  return trimmed || null;
};

const folderIdsEqual = (a?: string | null, b?: string | null) => getFolderId(a) === getFolderId(b);

const getValidFolderId = (
  folders: KnowledgeBaseFolderFieldsFragment[] = [],
  folderId?: string | null
) => {
  const normalizedFolderId = getFolderId(folderId);
  if (!normalizedFolderId) {
    return null;
  }

  return folders.some((folder) => folder.id === normalizedFolderId) ? normalizedFolderId : null;
};

const getFolderPath = (
  folders: KnowledgeBaseFolderFieldsFragment[] = [],
  folderId?: string | null
) => {
  const folderById = new Map(folders.map((folder) => [folder.id, folder]));
  const path: KnowledgeBaseFolderFieldsFragment[] = [];
  const seen = new Set<string>();
  let currentFolderId = getValidFolderId(folders, folderId);

  while (currentFolderId && !seen.has(currentFolderId)) {
    seen.add(currentFolderId);
    const folder = folderById.get(currentFolderId);
    if (!folder) {
      break;
    }
    path.unshift(folder);
    currentFolderId = getFolderId(folder.parentFolderId);
  }

  return path;
};

const getChildFolders = (
  folders: KnowledgeBaseFolderFieldsFragment[] = [],
  parentFolderId?: string | null
) =>
  folders
    .filter((folder) => folderIdsEqual(folder.parentFolderId, parentFolderId))
    .sort((a, b) => a.name.localeCompare(b.name));

const buildFolderOptions = (
  folders: KnowledgeBaseFolderFieldsFragment[] = [],
  parentFolderId?: string | null,
  depth = 0,
  seen = new Set<string>(),
  excludedFolderIds = new Set<string>()
): { folder: KnowledgeBaseFolderFieldsFragment; depth: number }[] =>
  getChildFolders(folders, parentFolderId).flatMap((folder) => {
    if (seen.has(folder.id) || excludedFolderIds.has(folder.id)) {
      return [];
    }

    const nextSeen = new Set(seen);
    nextSeen.add(folder.id);

    return [
      { folder, depth },
      ...buildFolderOptions(folders, folder.id, depth + 1, nextSeen, excludedFolderIds)
    ];
  });

const getFolderOptionLabel = (
  folders: KnowledgeBaseFolderFieldsFragment[],
  folder: KnowledgeBaseFolderFieldsFragment
) =>
  getFolderPath(folders, folder.id)
    .map((pathFolder) => pathFolder.name)
    .join(' / ');

const getDescendantFolderIds = (
  folders: KnowledgeBaseFolderFieldsFragment[] = [],
  folderId: string
) => {
  const descendantFolderIds = new Set<string>();
  const queue = [folderId];

  while (queue.length > 0) {
    const currentFolderId = queue.shift();
    if (!currentFolderId) {
      continue;
    }

    getChildFolders(folders, currentFolderId).forEach((childFolder) => {
      if (descendantFolderIds.has(childFolder.id)) {
        return;
      }
      descendantFolderIds.add(childFolder.id);
      queue.push(childFolder.id);
    });
  }

  return descendantFolderIds;
};

const getFolderSearch = (folderId?: string | null) => {
  const normalizedFolderId = getFolderId(folderId);
  return normalizedFolderId ? `?folder=${encodeURIComponent(normalizedFolderId)}` : '';
};

const getIndexingStatusLabel = (status: IndexingStatus) => {
  switch (status) {
    case IndexingStatus.Completed:
      return 'Completed';
    case IndexingStatus.Failed:
      return 'Failed';
    case IndexingStatus.Indexing:
      return 'Indexing';
    case IndexingStatus.Unknown:
      return 'Queued';
  }
};

const DocumentStatusBadge = ({ status }: { status: IndexingStatus }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [IndexingStatus.Completed]: 'green',
          [IndexingStatus.Failed]: 'red',
          [IndexingStatus.Indexing]: 'blue',
          [IndexingStatus.Unknown]: 'neutral'
        }[status] || 'neutral'
      }
    >
      {getIndexingStatusLabel(status)}
    </Badge>
  );
};

const KnowledgeBaseDocumentIcon = ({
  document,
  className = 'h-4 w-4 flex-none'
}: {
  document: KnowledgeBaseDocumentFieldsFragment;
  className?: string;
}) => {
  if (document.type === KnowledgeBaseDocumentType.Url) {
    return (
      <GlobeAltIconSolid className={clsx(className, 'text-neutral-500 dark:text-neutral-400')} />
    );
  }

  if (document.file) {
    return (
      <DocumentIconSolid className={clsx(className, 'text-neutral-500 dark:text-neutral-400')} />
    );
  }

  return (
    <DocumentTextIconSolid className={clsx(className, 'text-neutral-500 dark:text-neutral-400')} />
  );
};

const AsyncPageTitle = ({
  data,
  children
}: React.PropsWithChildren<{
  data?: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }>;
}>) => {
  return (
    <PageTitle
      title={
        <React.Suspense fallback="Knowledge Base">
          <Await resolve={data}>
            {(data) => {
              return data?.knowledgeBase?.title || 'Knowledge Base';
            }}
          </Await>
        </React.Suspense>
      }
      description="View and manage the knowledge Cara uses to operate"
    >
      <div className="space-y-4">{children}</div>
    </PageTitle>
  );
};

const AsyncSharedBanner = ({
  data
}: {
  data?: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }>;
}) => {
  return (
    <React.Suspense fallback={null}>
      <Await resolve={data}>
        {(data) =>
          !data?.knowledgeBase?.isOwner &&
          !!data?.knowledgeBase?.ownerAgency && (
            <div className="flex items-center gap-2 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 px-4 py-2">
              <ShareIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
              <span className="text-sm text-sky-700 dark:text-sky-300">
                This knowledge base is shared with you by{' '}
                <span className="font-medium">{data.knowledgeBase.ownerAgency.name}</span>
                {data.knowledgeBase.myAccess && (
                  <span className="text-sky-500 dark:text-sky-400">
                    {' '}
                    (
                    {data.knowledgeBase.myAccess.role === KnowledgeBaseAccessRole.Contributor
                      ? 'You can add and edit documents'
                      : 'View only'}
                    )
                  </span>
                )}
              </span>
            </div>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const AsyncSearchFilterInput = ({
  data,
  currentFolderId,
  onFiltersChange
}: {
  data?: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }>;
  currentFolderId?: string | null;
  onFiltersChange: (filters: ActiveFilter<KnowledgeBaseDocumentFilter>[]) => void;
}) => {
  return (
    <React.Suspense
      fallback={
        <SearchFilterInput
          filterDefinitions={[]}
          onFiltersChange={onFiltersChange}
          placeholder="Search by keyword, title..."
        />
      }
    >
      <Await resolve={data}>
        {(data) => {
          const knowledgeBase = data?.knowledgeBase;
          const activeFolderId = getValidFolderId(knowledgeBase?.folders || [], currentFolderId);
          const visibleDocuments = (knowledgeBase?.documents || []).filter((document) =>
            folderIdsEqual(document.folderId, activeFolderId)
          );

          return (
            <SearchFilterInput
              filterDefinitions={createFilterDefinitions(visibleDocuments)}
              onFiltersChange={onFiltersChange}
              placeholder="Search by keyword, title..."
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

const AsyncActionButtons = ({
  data,
  currentFolderId,
  viewMode,
  setViewMode,
  setCurrentFolder,
  setSettingsDialogOpen,
  setNewFolderDialogOpen,
  setUploadDialogOpen,
  setUrlDialogOpen,
  knowledgeBaseId
}: {
  data?: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }>;
  currentFolderId?: string | null;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  setCurrentFolder: (folderId: string | null) => void;
  setSettingsDialogOpen: (open: boolean) => void;
  setNewFolderDialogOpen: (open: boolean) => void;
  setUploadDialogOpen: (open: boolean) => void;
  setUrlDialogOpen: (open: boolean) => void;
  knowledgeBaseId: string;
}) => {
  // Three separate questions with three separate answers: a caller may be
  // allowed to upload files without being allowed to author a text document.
  const { hasAllPermissions } = usePermissions();
  const canManageBase = hasAllPermissions(MANAGE_KNOWLEDGE_BASE);
  const canCreateDocument = hasAllPermissions(CREATE_KB_DOCUMENT);
  const canUploadDocument = hasAllPermissions(UPLOAD_KB_DOCUMENT);
  const canWriteAnything = canManageBase || canCreateDocument || canUploadDocument;

  return (
    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        <React.Suspense fallback={null}>
          <RouterAwait resolve={data}>
            {(data) => {
              const knowledgeBase = data?.knowledgeBase;
              const folders = knowledgeBase?.folders || [];
              const activeFolderId = getValidFolderId(folders, currentFolderId);

              return (
                <FolderBreadcrumbs
                  folders={folders}
                  currentFolderId={activeFolderId}
                  onNavigate={setCurrentFolder}
                />
              );
            }}
          </RouterAwait>
        </React.Suspense>
      </div>

      {/* Actions */}
      <React.Suspense fallback={null}>
        <Await resolve={data}>
          {(data) => {
            const knowledgeBase = data?.knowledgeBase;
            const activeFolderId = getValidFolderId(knowledgeBase?.folders || [], currentFolderId);
            // Two independent gates: the access list says who is invited to
            // this base, the permission surface says whether the viewer's
            // agency role may write at all.
            const invited =
              knowledgeBase?.isOwner ||
              knowledgeBase?.myAccess?.role === KnowledgeBaseAccessRole.Contributor;
            const canEdit = invited && canWriteAnything;

            return (
              <div className="flex flex-none flex-row gap-2">
                {/* Settings Button (only for owners) */}
                {knowledgeBase?.isOwner && canManageBase && (
                  <Button color="white" onClick={() => setSettingsDialogOpen(true)}>
                    <Cog6ToothIcon className="h-4 w-4 mr-1.5" />
                    Settings
                  </Button>
                )}

                {/* Add menu (only for owners and contributors) */}
                {canEdit && (
                  <Menu as="div" className="relative">
                    <MenuButton as={Button} color="sky">
                      <PlusIcon className="h-4 w-4 mr-1.5" />
                      Add
                      <ChevronDownIcon className="h-4 w-4 ml-1" />
                    </MenuButton>
                    <MenuItems
                      transition
                      className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                    >
                      <div className="p-1">
                        {canManageBase && (
                          <>
                            <MenuItem>
                              <button
                                onClick={() => setNewFolderDialogOpen(true)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
                              >
                                <FolderIcon className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                                New Folder
                              </button>
                            </MenuItem>
                            <div className="my-1 border-t border-neutral-100 dark:border-neutral-700" />
                          </>
                        )}
                        {canUploadDocument && (
                          <MenuItem>
                            <button
                              onClick={() => setUploadDialogOpen(true)}
                              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
                            >
                              <ArrowUpTrayIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                              Upload Files
                            </button>
                          </MenuItem>
                        )}
                        {canCreateDocument && (
                          <>
                            <MenuItem>
                              <Link
                                to={`/app/knowledge/${knowledgeBaseId}/new${getFolderSearch(activeFolderId)}`}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
                              >
                                <PencilSquareIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                                Create Text Document
                              </Link>
                            </MenuItem>
                            <MenuItem>
                              <button
                                onClick={() => setUrlDialogOpen(true)}
                                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10"
                              >
                                <GlobeAltIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                                Track Website
                              </button>
                            </MenuItem>
                          </>
                        )}
                      </div>
                    </MenuItems>
                  </Menu>
                )}
              </div>
            );
          }}
        </Await>
      </React.Suspense>
    </div>
  );
};

const FolderBreadcrumbs = ({
  folders,
  currentFolderId,
  onNavigate
}: {
  folders: KnowledgeBaseFolderFieldsFragment[];
  currentFolderId?: string | null;
  onNavigate: (folderId: string | null) => void;
}) => {
  const path = getFolderPath(folders, currentFolderId);
  const leadingFolder = path.length > 3 ? path[0] : null;
  const trailingPath = path.length > 3 ? path.slice(-2) : path;
  const hiddenFolderCount = path.length > 3 ? path.length - 3 : 0;

  if (!path.length) {
    return null;
  }

  return (
    <nav
      className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden text-sm text-neutral-500 dark:text-neutral-400"
      aria-label="Folder path"
    >
      <button
        type="button"
        onClick={() => onNavigate(null)}
        aria-label="All documents"
        title="All documents"
        className="flex-none rounded-md p-1.5 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white"
      >
        <FolderIconSolid className="h-4 w-4" />
      </button>
      {leadingFolder && (
        <>
          <ChevronRightIcon className="h-3.5 w-3.5 flex-none text-neutral-300 dark:text-neutral-600" />
          <button
            type="button"
            onClick={() => onNavigate(leadingFolder.id)}
            title={leadingFolder.name}
            className="min-w-0 max-w-[8rem] flex-none truncate rounded-md px-2 py-1 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white sm:max-w-40"
          >
            {leadingFolder.name}
          </button>
        </>
      )}
      {hiddenFolderCount > 0 && (
        <>
          <ChevronRightIcon className="h-3.5 w-3.5 flex-none text-neutral-300 dark:text-neutral-600" />
          <span
            className="flex-none rounded-md px-2 py-1 text-neutral-400 dark:text-neutral-500"
            title={`${hiddenFolderCount} hidden ${hiddenFolderCount === 1 ? 'folder' : 'folders'}`}
          >
            ...
          </span>
        </>
      )}
      {trailingPath.map((folder, index) => (
        <React.Fragment key={folder.id}>
          <ChevronRightIcon className="h-3.5 w-3.5 flex-none text-neutral-300 dark:text-neutral-600" />
          <button
            type="button"
            onClick={() => onNavigate(folder.id)}
            title={folder.name}
            className={clsx(
              'min-w-0 truncate rounded-md px-2 py-1 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white',
              index === trailingPath.length - 1
                ? 'shrink text-left'
                : 'max-w-[8rem] flex-none sm:max-w-40',
              folder.id === currentFolderId && 'text-neutral-900 dark:text-white'
            )}
          >
            {folder.name}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
};

const ItemActionsMenu = ({
  label,
  onRename,
  onMove,
  onDelete
}: {
  label: string;
  onRename?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
}) => (
  <Menu as="div" className="relative">
    <MenuButton
      aria-label={`${label} actions`}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex size-8 items-center justify-center rounded-full text-neutral-500 dark:text-neutral-400 transition-colors hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white"
    >
      <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
    </MenuButton>
    <MenuItems
      transition
      anchor={{ to: 'bottom end', gap: 4 }}
      className="z-50 w-40 origin-top-right rounded-md bg-white dark:bg-neutral-800 p-1 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
      onClick={(e) => e.stopPropagation()}
    >
      {onRename && (
        <MenuItem>
          <button
            type="button"
            onClick={onRename}
            className="group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10 data-[focus]:text-neutral-900 dark:data-[focus]:text-white"
          >
            <PencilSquareIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400 group-data-[focus]:text-neutral-700 dark:group-data-[focus]:text-neutral-300" />
            Rename
          </button>
        </MenuItem>
      )}
      {onMove && (
        <MenuItem>
          <button
            type="button"
            onClick={onMove}
            className="group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 data-[focus]:bg-neutral-100 dark:data-[focus]:bg-white/10 data-[focus]:text-neutral-900 dark:data-[focus]:text-white"
          >
            <FolderIcon className="h-4 w-4 text-neutral-500 dark:text-neutral-400 group-data-[focus]:text-neutral-700 dark:group-data-[focus]:text-neutral-300" />
            Move
          </button>
        </MenuItem>
      )}
      {onDelete && (
        <MenuItem>
          <button
            type="button"
            onClick={onDelete}
            className="group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm text-rose-600 dark:text-rose-400 data-[focus]:bg-rose-50 dark:data-[focus]:bg-rose-500/10 data-[focus]:text-rose-700 dark:data-[focus]:text-rose-300"
          >
            <TrashIcon className="h-4 w-4 text-rose-500 dark:text-rose-400 group-data-[focus]:text-rose-600 dark:group-data-[focus]:text-rose-400" />
            Delete
          </button>
        </MenuItem>
      )}
    </MenuItems>
  </Menu>
);

const FolderCard = ({
  folder,
  onClick,
  canEdit = false,
  onRename,
  onMove,
  onDelete
}: {
  folder: KnowledgeBaseFolderFieldsFragment;
  onClick: () => void;
  canEdit?: boolean;
  onRename?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
}) => (
  <div className="relative rounded-lg bg-white dark:bg-neutral-800 shadow transition-all duration-300 hover:shadow-md">
    <button
      type="button"
      onClick={onClick}
      className="flex h-full min-h-[76px] w-full items-center gap-3 p-4 pr-12 text-left"
    >
      <FolderIconSolid className="h-4 w-4 flex-none text-neutral-500 dark:text-neutral-400" />
      <span
        className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-900 dark:text-white"
        title={folder.name}
      >
        {folder.name}
      </span>
    </button>
    {canEdit && (onRename || onMove || onDelete) && (
      <div className="absolute right-2 top-1/2 -translate-y-1/2">
        <ItemActionsMenu label="Folder" onRename={onRename} onMove={onMove} onDelete={onDelete} />
      </div>
    )}
  </div>
);

const FolderTableRow = ({
  folder,
  onClick,
  showSelectionColumn = false,
  selected = false,
  onSelectedChange,
  canEdit = false,
  onRename,
  onMove,
  onDelete
}: {
  folder: KnowledgeBaseFolderFieldsFragment;
  onClick: () => void;
  showSelectionColumn?: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  canEdit?: boolean;
  onRename?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
}) => (
  <TableRow
    onClick={onClick}
    className="hover:bg-neutral-950/[2.5%] dark:hover:bg-white/[2.5%] cursor-pointer"
  >
    {showSelectionColumn && (
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          color="sky"
          checked={selected}
          onChange={(checked) => onSelectedChange?.(Boolean(checked))}
          aria-label={`Select ${folder.name}`}
        />
      </TableCell>
    )}
    <TableCell>
      <div className="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)] gap-x-2 gap-y-1">
        <FolderIconSolid className="h-4 w-4 self-center text-neutral-500 dark:text-neutral-400" />
        <span className="truncate font-medium" title={folder.name}>
          {folder.name}
        </span>
        <span className="col-start-2 text-xs text-neutral-500 dark:text-neutral-400">Folder</span>
      </div>
    </TableCell>
    <TableCell />
    <TableCell />
    <TableCell>
      <div className="flex items-center justify-between gap-3">
        <span>{new Date(folder.updatedAt).toLocaleString()}</span>
        {canEdit && (onRename || onMove || onDelete) && (
          <ItemActionsMenu label="Folder" onRename={onRename} onMove={onMove} onDelete={onDelete} />
        )}
      </div>
    </TableCell>
  </TableRow>
);

const EmptyFolderState = ({ onUpload }: { onUpload: () => void }) => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 px-4 shadow sm:px-6">
    <div className="text-center py-12">
      <FolderIconSolid className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        This folder is empty
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 sm:max-w-md mx-auto">
        Add documents or create a folder here to organize this knowledge base.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <Button color="white" onClick={onUpload}>
          <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
          Upload Files
        </Button>
      </div>
    </div>
  </div>
);

const BulkSelectionActionBar = ({
  selectedDocumentCount,
  selectedFolderCount,
  onClear,
  onMove,
  onDelete
}: {
  selectedDocumentCount: number;
  selectedFolderCount: number;
  onClear: () => void;
  onMove: () => void;
  onDelete: () => void;
}) => (
  <AnimatePresence>
    {selectedDocumentCount + selectedFolderCount > 0 && (
      <motion.div
        className="fixed left-1/2 lg:left-[calc(8rem+50vw)] bottom-12 shadow-xl border border-neutral-100 dark:border-neutral-700 rounded-full bg-white dark:bg-neutral-800 p-2 z-10 flex gap-2"
        initial={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
        animate={{ opacity: 1, translateX: '-50%', translateY: 0, scale: 1 }}
        exit={{ opacity: 0, translateX: '-50%', translateY: 100, scale: 0.9 }}
        transition={{ duration: 0.15, ease: 'easeInOut' }}
      >
        <button
          type="button"
          className="flex flex-shrink-0 items-center gap-x-1 rounded-full border border-transparent bg-blue-600/80 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-600/90"
          onClick={onClear}
        >
          <div>{selectedDocumentCount + selectedFolderCount} selected</div>
          <XMarkIcon className="size-3.5" />
        </button>
        <button
          type="button"
          className="flex flex-shrink-0 items-center gap-x-1 rounded-full border border-dashed border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100/50 dark:hover:bg-white/10"
          onClick={onMove}
        >
          <FolderIcon className="size-3.5" />
          Move
        </button>
        <button
          type="button"
          className="flex flex-shrink-0 items-center gap-x-1 rounded-full border border-dashed border-rose-200 dark:border-rose-500/30 bg-white dark:bg-neutral-800 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10"
          onClick={onDelete}
        >
          <TrashIcon className="size-3.5" />
          Delete
        </button>
      </motion.div>
    )}
  </AnimatePresence>
);

const AsyncDocuments = ({
  data,
  currentFolderId,
  activeSearchFilters,
  viewMode,
  setSelectedDocument,
  setUploadDialogOpen,
  setCurrentFolder
}: {
  data?: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }>;
  currentFolderId?: string | null;
  activeSearchFilters: ActiveFilter<KnowledgeBaseDocumentFilter>[];
  viewMode: ViewMode;
  setSelectedDocument: (document: KnowledgeBaseDocumentFieldsFragment | null) => void;
  setUploadDialogOpen: (open: boolean) => void;
  setCurrentFolder: (folderId: string | null) => void;
}) => {
  // Three separate permissions, not one: folders and the bulk item actions are
  // updates on the knowledge base, renaming or moving a document is an update on
  // the document, and deleting one is a DELETE. Holding any of them must not
  // unlock the others.
  const { hasAllPermissions } = usePermissions();
  const canManageBase = hasAllPermissions(MANAGE_KNOWLEDGE_BASE);
  const canUpdateDocuments = hasAllPermissions(UPDATE_KB_DOCUMENT);
  const canDeleteDocuments = hasAllPermissions(DELETE_KB_DOCUMENT);
  const [selectedDocumentIds, setSelectedDocumentIds] = React.useState<string[]>([]);
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<string[]>([]);
  const [showBulkMoveDialog, setShowBulkMoveDialog] = React.useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = React.useState(false);
  const [folderToRename, setFolderToRename] =
    React.useState<KnowledgeBaseFolderFieldsFragment | null>(null);
  const [folderToDelete, setFolderToDelete] =
    React.useState<KnowledgeBaseFolderFieldsFragment | null>(null);
  const [folderToMove, setFolderToMove] = React.useState<KnowledgeBaseFolderFieldsFragment | null>(
    null
  );
  const [documentToDelete, setDocumentToDelete] =
    React.useState<KnowledgeBaseDocumentFieldsFragment | null>(null);
  const [documentToRename, setDocumentToRename] =
    React.useState<KnowledgeBaseDocumentFieldsFragment | null>(null);
  const [documentToMove, setDocumentToMove] =
    React.useState<KnowledgeBaseDocumentFieldsFragment | null>(null);
  const revalidator = useRevalidator();

  React.useEffect(() => {
    setShowBulkMoveDialog(false);
    setShowBulkDeleteDialog(false);
    setFolderToRename(null);
    setFolderToDelete(null);
    setFolderToMove(null);
    setDocumentToDelete(null);
    setDocumentToRename(null);
    setDocumentToMove(null);
    setSelectedDocumentIds([]);
    setSelectedFolderIds([]);
  }, [currentFolderId, activeSearchFilters, viewMode]);

  const toggleSelectedDocument = React.useCallback((documentId: string, selected: boolean) => {
    setSelectedDocumentIds((currentIds) => {
      if (selected) {
        return currentIds.includes(documentId) ? currentIds : [...currentIds, documentId];
      }

      return currentIds.filter((id) => id !== documentId);
    });
  }, []);

  const toggleSelectedFolder = React.useCallback((folderId: string, selected: boolean) => {
    setSelectedFolderIds((currentIds) => {
      if (selected) {
        return currentIds.includes(folderId) ? currentIds : [...currentIds, folderId];
      }

      return currentIds.filter((id) => id !== folderId);
    });
  }, []);

  return (
    <React.Suspense fallback={<KnowledgeBaseDocumentsLoader />}>
      <Await resolve={data}>
        {(data) => {
          if (!data?.knowledgeBase) {
            return null;
          }

          const knowledgeBase = data.knowledgeBase;
          const invited =
            knowledgeBase.isOwner ||
            knowledgeBase.myAccess?.role === KnowledgeBaseAccessRole.Contributor;
          // Folder actions and everything the selection bar offers
          // (moveKnowledgeBaseItemsToFolder, deleteKnowledgeBaseItems) are
          // knowledge-base updates.
          const canEditFolders = invited && canManageBase;
          const canRenameOrMoveDocuments = invited && canUpdateDocuments;
          const canDeleteDocument = invited && canDeleteDocuments;
          const canEditDocuments = canRenameOrMoveDocuments || canDeleteDocument;
          const showSelectionColumn = canEditFolders && viewMode === 'list';

          // Apply search filters (keywords, title search)
          const documents = knowledgeBase.documents || [];
          const folders = knowledgeBase.folders || [];
          const activeFolderId = getValidFolderId(folders, currentFolderId);
          const childFolders = getChildFolders(folders, activeFolderId);
          const visibleDocuments = documents.filter((document) =>
            folderIdsEqual(document.folderId, activeFolderId)
          );
          const filteredDocuments = applyFilters(visibleDocuments, activeSearchFilters);
          const hasActiveFilters = activeSearchFilters.length > 0;
          const hasCurrentFolderContent = childFolders.length > 0 || visibleDocuments.length > 0;
          const selectedDocumentIdSet = new Set(selectedDocumentIds);
          const selectedFolderIdSet = new Set(selectedFolderIds);
          const filteredDocumentIds = filteredDocuments.map((document) => document.id);
          const childFolderIds = childFolders.map((folder) => folder.id);
          const visibleSelectableCount = filteredDocumentIds.length + childFolderIds.length;
          const allVisibleItemsSelected =
            visibleSelectableCount > 0 &&
            filteredDocumentIds.every((id) => selectedDocumentIdSet.has(id)) &&
            childFolderIds.every((id) => selectedFolderIdSet.has(id));

          const toggleAllVisibleItems = (selected: boolean) => {
            const filteredDocumentIdSet = new Set(filteredDocumentIds);
            const childFolderIdSet = new Set(childFolderIds);
            setSelectedDocumentIds((currentIds) => {
              if (!selected) {
                return currentIds.filter((id) => !filteredDocumentIdSet.has(id));
              }

              const nextIds = [...currentIds];
              filteredDocumentIds.forEach((id) => {
                if (!nextIds.includes(id)) {
                  nextIds.push(id);
                }
              });
              return nextIds;
            });
            setSelectedFolderIds((currentIds) => {
              if (!selected) {
                return currentIds.filter((id) => !childFolderIdSet.has(id));
              }

              const nextIds = [...currentIds];
              childFolderIds.forEach((id) => {
                if (!nextIds.includes(id)) {
                  nextIds.push(id);
                }
              });
              return nextIds;
            });
          };

          return (
            <div className="space-y-4">
              <KnowledgeBaseIndexingRefresh documents={documents} />
              <BulkSelectionActionBar
                selectedDocumentCount={showSelectionColumn ? selectedDocumentIds.length : 0}
                selectedFolderCount={showSelectionColumn ? selectedFolderIds.length : 0}
                onClear={() => {
                  setSelectedDocumentIds([]);
                  setSelectedFolderIds([]);
                }}
                onMove={() => setShowBulkMoveDialog(true)}
                onDelete={() => setShowBulkDeleteDialog(true)}
              />

              {!documents.length && !folders.length ? (
                <EmptyState onUpload={() => setUploadDialogOpen(true)} />
              ) : !hasCurrentFolderContent ? (
                <EmptyFolderState onUpload={() => setUploadDialogOpen(true)} />
              ) : !filteredDocuments.length && hasActiveFilters && !childFolders.length ? (
                <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-12 text-center">
                  <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600" />
                  <h3 className="mt-4 text-sm font-semibold text-neutral-900 dark:text-white">
                    No matching documents
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                    Try adjusting your search or filters
                  </p>
                </div>
              ) : viewMode === 'card' ? (
                <div className="space-y-6">
                  {childFolders.length > 0 && (
                    <section className="space-y-3">
                      <h2 className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Folders
                      </h2>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {childFolders.map((folder) => (
                          <FolderCard
                            key={folder.id}
                            folder={folder}
                            onClick={() => setCurrentFolder(folder.id)}
                            canEdit={canEditFolders}
                            onRename={() => setFolderToRename(folder)}
                            onMove={() => setFolderToMove(folder)}
                            onDelete={() => setFolderToDelete(folder)}
                          />
                        ))}
                      </div>
                    </section>
                  )}
                  {filteredDocuments.length > 0 && (
                    <section className="space-y-3">
                      <h2 className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                        Documents
                      </h2>
                      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                        {filteredDocuments.map((document) => (
                          <DocumentCard
                            key={document.id}
                            document={document}
                            onClick={() => setSelectedDocument(document)}
                            canEdit={canEditDocuments}
                            onRename={
                              canRenameOrMoveDocuments &&
                              document.type === KnowledgeBaseDocumentType.File
                                ? () => setDocumentToRename(document)
                                : undefined
                            }
                            onMove={
                              canRenameOrMoveDocuments
                                ? () => setDocumentToMove(document)
                                : undefined
                            }
                            onDelete={
                              canDeleteDocument ? () => setDocumentToDelete(document) : undefined
                            }
                          />
                        ))}
                      </div>
                    </section>
                  )}
                </div>
              ) : (
                <div className="relative px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        {showSelectionColumn && (
                          <TableHeader className="w-10 py-4">
                            {visibleSelectableCount > 0 && (
                              <Checkbox
                                color="sky"
                                checked={allVisibleItemsSelected}
                                onChange={(checked) => toggleAllVisibleItems(Boolean(checked))}
                                aria-label="Select visible items"
                              />
                            )}
                          </TableHeader>
                        )}
                        <TableHeader className="py-4">Title</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Uploaded By</TableHeader>
                        <TableHeader className="py-4">Last Updated</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {childFolders.map((folder) => (
                        <FolderTableRow
                          key={folder.id}
                          folder={folder}
                          onClick={() => setCurrentFolder(folder.id)}
                          showSelectionColumn={showSelectionColumn}
                          selected={selectedFolderIdSet.has(folder.id)}
                          onSelectedChange={(selected) => toggleSelectedFolder(folder.id, selected)}
                          canEdit={canEditFolders}
                          onRename={() => setFolderToRename(folder)}
                          onMove={() => setFolderToMove(folder)}
                          onDelete={() => setFolderToDelete(folder)}
                        />
                      ))}
                      {filteredDocuments.map((document) => {
                        const displayTitle = getKnowledgeBaseDocumentDisplayTitle(document);
                        return (
                          <TableRow
                            key={document.id}
                            onClick={() => setSelectedDocument(document)}
                            className="hover:bg-neutral-950/[2.5%] dark:hover:bg-white/[2.5%] cursor-pointer"
                          >
                            {showSelectionColumn && (
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  color="sky"
                                  checked={selectedDocumentIdSet.has(document.id)}
                                  onChange={(checked) =>
                                    toggleSelectedDocument(document.id, Boolean(checked))
                                  }
                                  aria-label={`Select ${displayTitle}`}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="grid min-w-0 grid-cols-[1rem_minmax(0,1fr)] gap-x-2 gap-y-1">
                                <KnowledgeBaseDocumentIcon
                                  document={document}
                                  className="h-4 w-4 self-center"
                                />
                                <span className="truncate font-medium" title={displayTitle}>
                                  {displayTitle}
                                </span>
                                <span className="col-start-2 truncate text-xs text-neutral-500 dark:text-neutral-400">
                                  {document.file?.name ||
                                    (document.type === KnowledgeBaseDocumentType.Url
                                      ? 'Tracked website'
                                      : 'Text document')}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <DocumentStatusBadge status={document.indexingStatus.status} />
                            </TableCell>
                            <TableCell>{document.createdBy.name}</TableCell>
                            <TableCell>
                              <div className="flex items-center justify-between gap-3">
                                <span>{new Date(document.createdAt).toLocaleString()}</span>
                                {canEditDocuments && (
                                  <ItemActionsMenu
                                    label="Document"
                                    onRename={
                                      canRenameOrMoveDocuments &&
                                      document.type === KnowledgeBaseDocumentType.File
                                        ? () => setDocumentToRename(document)
                                        : undefined
                                    }
                                    onMove={
                                      canRenameOrMoveDocuments
                                        ? () => setDocumentToMove(document)
                                        : undefined
                                    }
                                    onDelete={
                                      canDeleteDocument
                                        ? () => setDocumentToDelete(document)
                                        : undefined
                                    }
                                  />
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              <BulkMoveKnowledgeBaseItemsDialog
                open={
                  showBulkMoveDialog && selectedDocumentIds.length + selectedFolderIds.length > 0
                }
                knowledgeBaseId={knowledgeBase.id}
                documentIds={selectedDocumentIds}
                folderIds={selectedFolderIds}
                folders={folders}
                initialFolderId={activeFolderId}
                onClose={() => setShowBulkMoveDialog(false)}
                onSuccess={() => {
                  setSelectedDocumentIds([]);
                  setSelectedFolderIds([]);
                  setShowBulkMoveDialog(false);
                  revalidator.revalidate();
                }}
              />
              <BulkDeleteKnowledgeBaseItemsDialog
                open={
                  showBulkDeleteDialog && selectedDocumentIds.length + selectedFolderIds.length > 0
                }
                knowledgeBaseId={knowledgeBase.id}
                documentIds={selectedDocumentIds}
                folderIds={selectedFolderIds}
                onClose={() => setShowBulkDeleteDialog(false)}
                onSuccess={() => {
                  setSelectedDocumentIds([]);
                  setSelectedFolderIds([]);
                  setShowBulkDeleteDialog(false);
                  revalidator.revalidate();
                }}
              />
              <BulkMoveDocumentsDialog
                open={!!documentToMove}
                documentIds={documentToMove ? [documentToMove.id] : []}
                folders={folders}
                initialFolderId={documentToMove?.folderId}
                onClose={() => setDocumentToMove(null)}
                onSuccess={() => {
                  setDocumentToMove(null);
                  revalidator.revalidate();
                }}
              />
              <MoveKnowledgeBaseFolderDialog
                open={!!folderToMove}
                knowledgeBaseId={knowledgeBase.id}
                folder={folderToMove}
                folders={folders}
                onClose={() => setFolderToMove(null)}
                onSuccess={() => {
                  setFolderToMove(null);
                  revalidator.revalidate();
                }}
              />
              <RenameFolderDialog
                open={!!folderToRename}
                knowledgeBaseId={knowledgeBase.id}
                folder={folderToRename}
                onClose={() => setFolderToRename(null)}
                onSuccess={() => {
                  setFolderToRename(null);
                  revalidator.revalidate();
                }}
              />
              <RenameDocumentDialog
                open={!!documentToRename}
                document={documentToRename}
                onClose={() => setDocumentToRename(null)}
                onSuccess={() => {
                  setDocumentToRename(null);
                  revalidator.revalidate();
                }}
              />
              <DeleteKnowledgeBaseDocumentDialog
                open={!!documentToDelete}
                document={documentToDelete}
                onClose={() => setDocumentToDelete(null)}
                onSuccess={() => {
                  setDocumentToDelete(null);
                  revalidator.revalidate();
                }}
              />
              {canEditFolders && (
                <DeleteFolderDialog
                  open={!!folderToDelete}
                  knowledgeBaseId={knowledgeBase.id}
                  folder={folderToDelete}
                  onClose={() => setFolderToDelete(null)}
                  onSuccess={() => {
                    setFolderToDelete(null);
                    revalidator.revalidate();
                  }}
                />
              )}
            </div>
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const KnowledgeBasePage = () => {
  const outlet = useOutlet();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();

  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }> }
    | undefined;

  const [activeSearchFilters, setActiveSearchFilters] = React.useState<
    ActiveFilter<KnowledgeBaseDocumentFilter>[]
  >([]);
  const [viewMode, setViewMode] = React.useState<ViewMode>('card');
  const currentFolderId = searchParams.get('folder');

  const setCurrentFolder = React.useCallback(
    (folderId: string | null) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const normalizedFolderId = getFolderId(folderId);
      if (normalizedFolderId) {
        nextSearchParams.set('folder', normalizedFolderId);
      } else {
        nextSearchParams.delete('folder');
      }
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const setSelectedDocument = (document: KnowledgeBaseDocumentFieldsFragment | null) => {
    navigate(`/app/knowledge/${knowledgeBaseId}/${document?.id}${location.search}`);
  };

  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = React.useState(false);
  const [newFolderDialogOpen, setNewFolderDialogOpen] = React.useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);

  const canWrite = usePermissions().hasAnyPermission(ANY_KB_WRITE);

  return (
    <AsyncPageTitle data={loader?.data}>
      {!canWrite && (
        <PermissionNotice>
          You can read knowledge bases but not change them. Ask an agency admin for access.
        </PermissionNotice>
      )}
      <div className="space-y-2">
        <AsyncSearchFilterInput
          data={loader?.data}
          currentFolderId={currentFolderId}
          onFiltersChange={setActiveSearchFilters}
        />
        <AsyncActionButtons
          data={loader?.data}
          currentFolderId={currentFolderId}
          viewMode={viewMode}
          setViewMode={setViewMode}
          setCurrentFolder={setCurrentFolder}
          setSettingsDialogOpen={setSettingsDialogOpen}
          setNewFolderDialogOpen={setNewFolderDialogOpen}
          setUploadDialogOpen={setUploadDialogOpen}
          setUrlDialogOpen={setUrlDialogOpen}
          knowledgeBaseId={knowledgeBaseId || ''}
        />
      </div>
      <AsyncSharedBanner data={loader?.data} />
      <AsyncDocuments
        data={loader?.data}
        currentFolderId={currentFolderId}
        activeSearchFilters={activeSearchFilters}
        viewMode={viewMode}
        setSelectedDocument={setSelectedDocument}
        setUploadDialogOpen={setUploadDialogOpen}
        setCurrentFolder={setCurrentFolder}
      />

      <React.Suspense fallback={null}>
        <Await resolve={loader?.data}>
          {(data) => {
            const activeFolderId = getValidFolderId(
              data?.knowledgeBase?.folders || [],
              currentFolderId
            );

            return (
              <>
                {/* New Folder Dialog */}
                <NewFolderDialog
                  open={newFolderDialogOpen && !!knowledgeBaseId}
                  knowledgeBaseId={knowledgeBaseId}
                  folders={data?.knowledgeBase?.folders || []}
                  parentFolderId={activeFolderId}
                  onClose={() => setNewFolderDialogOpen(false)}
                  onSuccess={() => revalidator.revalidate()}
                />

                {/* Upload Dialog */}
                <KnowledgeBaseUploadDialog
                  open={uploadDialogOpen && !!knowledgeBaseId}
                  knowledgeBaseId={knowledgeBaseId || ''}
                  folderId={activeFolderId}
                  onClose={() => setUploadDialogOpen(false)}
                />

                {/* Track Website Dialog */}
                <TrackWebsiteDialog
                  open={urlDialogOpen && !!knowledgeBaseId}
                  knowledgeBaseId={knowledgeBaseId}
                  folderId={activeFolderId}
                  onClose={() => setUrlDialogOpen(false)}
                />
              </>
            );
          }}
        </Await>
      </React.Suspense>

      {/* Settings Dialog (only for owners) */}

      <React.Suspense fallback={null}>
        <Await resolve={loader?.data}>
          {(data) =>
            data?.knowledgeBase && (
              <KnowledgeBaseSettingsDialog
                open={settingsDialogOpen}
                knowledgeBaseId={data.knowledgeBase.id}
                knowledgeBaseTitle={data.knowledgeBase.title}
                guidedRetrievalEnabled={data.knowledgeBase.guidedRetrievalEnabled}
                accessList={data.knowledgeBase.accessList || []}
                onClose={() => setSettingsDialogOpen(false)}
                onUpdate={() => revalidator.revalidate()} // Refresh to get updated access list
              />
            )
          }
        </Await>
      </React.Suspense>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, {})}
      </AnimatePresence>
    </AsyncPageTitle>
  );
};

// Document Card Component
const DocumentCard = ({
  document,
  onClick,
  canEdit = false,
  onRename,
  onMove,
  onDelete,
  selectable = false,
  selected = false,
  onSelectedChange
}: {
  document: KnowledgeBaseDocumentFieldsFragment;
  onClick: () => void;
  canEdit?: boolean;
  onRename?: () => void;
  onMove?: () => void;
  onDelete?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}) => {
  const isFile = !!document.file;
  const status = document.indexingStatus.status;
  const showStatus = status !== IndexingStatus.Completed;
  const displayTitle = getKnowledgeBaseDocumentDisplayTitle(document);

  return (
    <div
      className={clsx(
        'relative rounded-lg bg-white dark:bg-neutral-800 shadow hover:shadow-xl transition-all duration-300 w-full h-full',
        selected && 'ring-2 ring-sky-400'
      )}
    >
      {selectable && (
        <div className="absolute right-4 top-4 z-10">
          <Checkbox
            color="sky"
            checked={selected}
            onChange={(checked) => onSelectedChange?.(Boolean(checked))}
            aria-label={`Select ${displayTitle}`}
          />
        </div>
      )}
      <button
        onClick={onClick}
        className={clsx('flex h-full w-full flex-col p-6 text-left', canEdit && 'pr-14')}
      >
        <div
          className={clsx(
            'flex items-start justify-between gap-3',
            (selectable || canEdit) && 'pr-8'
          )}
        >
          <h3 className="text-base/6 font-body m-0 flex min-w-0 flex-row items-center gap-2 p-0 font-medium">
            <KnowledgeBaseDocumentIcon document={document} />
            <span className="truncate" title={displayTitle}>
              {displayTitle}
            </span>
          </h3>
          {showStatus && <DocumentStatusBadge status={status} />}
        </div>

        {document.metadata?.summary && (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2">
            {document.metadata.summary}
          </p>
        )}

        <div className="flex-1" />

        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="truncate">
            {isFile
              ? document.file?.name
              : document.type === KnowledgeBaseDocumentType.Url
                ? 'Tracked website'
                : 'Text document'}
          </span>
          <span className="flex-none">
            {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
              new Date(document.createdAt)
            )}
          </span>
        </div>
      </button>
      {canEdit && (onRename || onMove || onDelete) && (
        <div className="absolute right-3 top-3">
          <ItemActionsMenu
            label="Document"
            onRename={onRename}
            onMove={onMove}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onUpload }: { onUpload: () => void }) => (
  <div className="px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
    <div className="text-center py-12">
      <CircleStackIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        No documents yet
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 sm:max-w-md mx-auto">
        Upload files or create documents to build your knowledge base. Cara will use these to
        provide better assistance.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <Button color="sky" onClick={onUpload}>
          <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
          Upload Files
        </Button>
      </div>
    </div>
  </div>
);

const FolderLocationPreview = ({
  folders,
  folderId,
  label = 'Location'
}: {
  folders: KnowledgeBaseFolderFieldsFragment[];
  folderId?: string | null;
  label?: string;
}) => {
  const folderPath = getFolderPath(folders, folderId);
  const visibleFolderPath =
    folderPath.length > 4
      ? [
          { id: folderPath[0].id, name: folderPath[0].name, type: 'folder' as const },
          { id: '__hidden_folders__', name: '...', type: 'ellipsis' as const },
          ...folderPath.slice(-2).map((folder) => ({
            id: folder.id,
            name: folder.name,
            type: 'folder' as const
          }))
        ]
      : folderPath.map((folder) => ({ id: folder.id, name: folder.name, type: 'folder' as const }));

  return (
    <div className="w-full min-w-0 max-w-full overflow-hidden">
      <div className="text-sm font-medium text-neutral-900 dark:text-white">{label}</div>
      <div className="mt-2 max-h-40 space-y-1 overflow-y-auto pr-1 text-sm text-neutral-600 dark:text-neutral-400">
        <div className="flex min-w-0 items-center gap-2">
          <FolderIconSolid className="h-4 w-4 flex-none text-neutral-500 dark:text-neutral-400" />
          <span className="min-w-0 truncate font-medium text-neutral-700 dark:text-neutral-300">
            All documents
          </span>
        </div>
        {visibleFolderPath.map((folder, index) => (
          <div
            key={folder.id}
            className="flex min-w-0 items-center gap-2"
            style={{ paddingLeft: `${(index + 1) * 16}px` }}
          >
            <span className="flex-none font-mono text-xs text-neutral-300 dark:text-neutral-600">
              └─
            </span>
            <span
              className={clsx(
                'min-w-0 truncate',
                folder.type === 'ellipsis' && 'flex-none text-neutral-400 dark:text-neutral-500',
                index === visibleFolderPath.length - 1
                  ? 'font-medium text-neutral-800 dark:text-neutral-200'
                  : 'text-neutral-600 dark:text-neutral-400'
              )}
              title={folder.type === 'ellipsis' ? 'Hidden folders' : folder.name}
            >
              {folder.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const NewFolderDialog = ({
  open,
  knowledgeBaseId,
  folders,
  parentFolderId,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId?: string;
  folders: KnowledgeBaseFolderFieldsFragment[];
  parentFolderId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await client.mutate<CreateKnowledgeBaseFolderMutation>({
        mutation: CreateKnowledgeBaseFolderDocument,
        variables: {
          knowledgeBaseId: knowledgeBaseId || '',
          name,
          parentFolderId: parentFolderId || null
        }
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>New folder</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset className="min-w-0">
            <FieldGroup className="min-w-0">
              <FolderLocationPreview folders={folders} folderId={parentFolderId} />
              <Field className="min-w-0">
                <Label>Name</Label>
                <Input
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.currentTarget.value)}
                  required
                />
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={isSubmitting} disabled={!name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const RenameDocumentDialog = ({
  open,
  document,
  onClose,
  onSuccess
}: {
  open: boolean;
  document: KnowledgeBaseDocumentFieldsFragment | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const displayedTitle = document ? getKnowledgeBaseDocumentDisplayTitle(document) : '';
  const updateState = resolveKnowledgeBaseDocumentDisplayNameUpdate({
    input: name,
    storedDisplayName: document?.displayName,
    displayedTitle
  });

  React.useEffect(() => {
    if (open && document) {
      setName(getKnowledgeBaseDocumentDisplayTitle(document));
      setError(null);
      setIsSubmitting(false);
    }
  }, [open, document]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!document || !updateState.canSubmit) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await client.mutate<UpdateKnowledgeBaseDocumentMutation>({
        mutation: UpdateKnowledgeBaseDocumentDocument,
        variables: {
          id: document.id,
          input: {
            displayName: updateState.submitDisplayName
          }
        }
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors.map((err) => err.message).join(', '));
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename document');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Rename file</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                {!!document?.displayName?.trim() && (
                  <Description>Clear to restore the default name.</Description>
                )}
                <Input value={name} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
              </Field>
              {updateState.isOverLimit && (
                <ErrorMessage>Name must be 100 characters or fewer</ErrorMessage>
              )}
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="sky"
            loading={isSubmitting}
            disabled={!updateState.canSubmit}
          >
            {updateState.canSubmit && updateState.submitDisplayName === '' ? 'Reset' : 'Rename'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const RenameFolderDialog = ({
  open,
  knowledgeBaseId,
  folder,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId: string;
  folder: KnowledgeBaseFolderFieldsFragment | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [name, setName] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && folder) {
      setName(folder.name);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open, folder]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!folder) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await client.mutate<RenameKnowledgeBaseFolderMutation>({
        mutation: RenameKnowledgeBaseFolderDocument,
        variables: {
          knowledgeBaseId,
          folderId: folder.id,
          name
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Rename folder</DialogTitle>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  value={name}
                  maxLength={100}
                  onChange={(e) => setName(e.currentTarget.value)}
                  required
                />
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="sky"
            loading={isSubmitting}
            disabled={!name.trim() || name.trim() === folder?.name}
          >
            Rename
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const DeleteFolderDialog = ({
  open,
  knowledgeBaseId,
  folder,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId: string;
  folder: KnowledgeBaseFolderFieldsFragment | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setIsDeleting(false);
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!folder) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await client.mutate<DeleteKnowledgeBaseFolderMutation>({
        mutation: DeleteKnowledgeBaseFolderDocument,
        variables: {
          knowledgeBaseId,
          folderId: folder.id
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete folder');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Delete folder</DialogTitle>
      <DialogDescription>
        Delete <span className="font-semibold">{folder?.name}</span>? Only empty folders can be
        deleted.
      </DialogDescription>
      {error && (
        <DialogBody>
          <ErrorMessage>{error}</ErrorMessage>
        </DialogBody>
      )}
      <DialogActions>
        <Button type="button" color="white" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button type="button" color="red" onClick={handleDelete} loading={isDeleting}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DeleteKnowledgeBaseDocumentDialog = ({
  open,
  document,
  onClose,
  onSuccess
}: {
  open: boolean;
  document: KnowledgeBaseDocumentFieldsFragment | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setIsDeleting(false);
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!document) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await client.mutate<DeleteKnowledgeBaseDocumentMutation>({
        mutation: DeleteKnowledgeBaseDocumentDocument,
        variables: {
          id: document.id
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Delete document</DialogTitle>
      <DialogDescription>
        Delete{' '}
        <span className="font-semibold">
          {document ? getKnowledgeBaseDocumentDisplayTitle(document) : ''}
        </span>
        ? This action is permanent.
      </DialogDescription>
      {error && (
        <DialogBody>
          <ErrorMessage>{error}</ErrorMessage>
        </DialogBody>
      )}
      <DialogActions>
        <Button type="button" color="white" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button type="button" color="red" onClick={handleDelete} loading={isDeleting}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkDeleteKnowledgeBaseItemsDialog = ({
  open,
  knowledgeBaseId,
  documentIds,
  folderIds,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId: string;
  documentIds: string[];
  folderIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selectedCount = documentIds.length + folderIds.length;

  React.useEffect(() => {
    if (!open) {
      setIsDeleting(false);
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!selectedCount) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await client.mutate<DeleteKnowledgeBaseItemsMutation>({
        mutation: DeleteKnowledgeBaseItemsDocument,
        variables: {
          knowledgeBaseId,
          documentIds,
          folderIds
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete selected items');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Delete selected items</DialogTitle>
      <DialogDescription>
        Delete {selectedCount} selected {selectedCount === 1 ? 'item' : 'items'}? Selected folders
        must be empty after the selected documents and folders are deleted.
      </DialogDescription>
      {error && (
        <DialogBody>
          <ErrorMessage>{error}</ErrorMessage>
        </DialogBody>
      )}
      <DialogActions>
        <Button type="button" color="white" onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button type="button" color="red" onClick={handleDelete} loading={isDeleting}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkMoveKnowledgeBaseItemsDialog = ({
  open,
  knowledgeBaseId,
  documentIds,
  folderIds,
  folders,
  initialFolderId,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId: string;
  documentIds: string[];
  folderIds: string[];
  folders: KnowledgeBaseFolderFieldsFragment[];
  initialFolderId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [destinationFolderId, setDestinationFolderId] = React.useState(
    getFolderId(initialFolderId) || ''
  );
  const [isMoving, setIsMoving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const selectedCount = documentIds.length + folderIds.length;

  React.useEffect(() => {
    if (open) {
      setDestinationFolderId(getFolderId(initialFolderId) || '');
      setError(null);
      setIsMoving(false);
    }
  }, [open, initialFolderId]);

  const excludedFolderIds = React.useMemo(() => {
    const excluded = new Set<string>();
    folderIds.forEach((folderId) => {
      excluded.add(folderId);
      getDescendantFolderIds(folders, folderId).forEach((descendantFolderId) =>
        excluded.add(descendantFolderId)
      );
    });
    return excluded;
  }, [folderIds, folders]);

  const folderOptions = React.useMemo(
    () => buildFolderOptions(folders, undefined, 0, new Set<string>(), excludedFolderIds),
    [folders, excludedFolderIds]
  );

  const handleMove = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCount) {
      return;
    }

    setIsMoving(true);
    setError(null);

    try {
      await client.mutate<MoveKnowledgeBaseItemsToFolderMutation>({
        mutation: MoveKnowledgeBaseItemsToFolderDocument,
        variables: {
          knowledgeBaseId,
          documentIds,
          folderIds,
          folderId: destinationFolderId || null
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move selected items');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Move selected items</DialogTitle>
      <DialogDescription>
        Choose a destination for {selectedCount} selected {selectedCount === 1 ? 'item' : 'items'}.
      </DialogDescription>
      <Form onSubmit={handleMove}>
        <DialogBody>
          <Fieldset className="min-w-0">
            <FieldGroup className="min-w-0">
              <Field className="min-w-0">
                <Label>Destination</Label>
                <Select
                  className="min-w-0 max-w-full"
                  value={destinationFolderId}
                  onChange={(e) => setDestinationFolderId(e.currentTarget.value)}
                >
                  <option value="">All documents</option>
                  {folderOptions.map(({ folder }) => (
                    <option key={folder.id} value={folder.id}>
                      {getFolderOptionLabel(folders, folder)}
                    </option>
                  ))}
                </Select>
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={isMoving}>
            Move
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const MoveKnowledgeBaseFolderDialog = ({
  open,
  knowledgeBaseId,
  folder,
  folders,
  onClose,
  onSuccess
}: {
  open: boolean;
  knowledgeBaseId: string;
  folder: KnowledgeBaseFolderFieldsFragment | null;
  folders: KnowledgeBaseFolderFieldsFragment[];
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [destinationFolderId, setDestinationFolderId] = React.useState('');
  const [isMoving, setIsMoving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDestinationFolderId(getFolderId(folder?.parentFolderId) || '');
      setError(null);
      setIsMoving(false);
    }
  }, [open, folder?.parentFolderId]);

  const excludedFolderIds = React.useMemo(() => {
    if (!folder) {
      return new Set<string>();
    }

    const excluded = getDescendantFolderIds(folders, folder.id);
    excluded.add(folder.id);
    return excluded;
  }, [folder, folders]);

  const folderOptions = React.useMemo(
    () => buildFolderOptions(folders, undefined, 0, new Set<string>(), excludedFolderIds),
    [folders, excludedFolderIds]
  );

  const handleMove = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!folder) {
      return;
    }

    setIsMoving(true);
    setError(null);

    try {
      await client.mutate<MoveKnowledgeBaseFolderMutation>({
        mutation: MoveKnowledgeBaseFolderDocument,
        variables: {
          knowledgeBaseId,
          folderId: folder.id,
          parentFolderId: destinationFolderId || null
        }
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move folder');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Move folder</DialogTitle>
      <DialogDescription>
        Choose a destination for <span className="font-semibold">{folder?.name}</span>.
      </DialogDescription>
      <Form onSubmit={handleMove}>
        <DialogBody>
          <Fieldset className="min-w-0">
            <FieldGroup className="min-w-0">
              <Field className="min-w-0">
                <Label>Destination</Label>
                <Select
                  className="min-w-0 max-w-full"
                  value={destinationFolderId}
                  onChange={(e) => setDestinationFolderId(e.currentTarget.value)}
                >
                  <option value="">All documents</option>
                  {folderOptions.map(({ folder }) => (
                    <option key={folder.id} value={folder.id}>
                      {getFolderOptionLabel(folders, folder)}
                    </option>
                  ))}
                </Select>
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={isMoving}>
            Move
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

// Replace Document Dialog
const ReplaceDocumentDialog = ({
  open,
  documentId,
  documentTitle,
  onClose,
  onSuccess
}: {
  open: boolean;
  documentId: string;
  documentTitle: string;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState<CopilotActionError>();

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setFile(null);
      setError(undefined);
      setIsUploading(false);
    }
  }, [open]);

  const handleFileChange = (change: { files: CaraWorkspaceFileInput[] }) => {
    setFile(change.files[0]?.upload || null);
    setError(undefined);
  };

  const handleReplace = async () => {
    if (!file) {
      setError({ kind: 'generic', message: 'Please select a file' });
      return;
    }

    setIsUploading(true);
    setError(undefined);

    try {
      await client.mutate<ReplaceKnowledgeBaseDocumentMutation>({
        mutation: ReplaceKnowledgeBaseDocumentDocument,
        variables: {
          documentId,
          file
        }
      });
      onSuccess();
    } catch (err) {
      const applicationError = getApplicationError(
        err instanceof ApolloError ? err : new ApolloError({ graphQLErrors: [] })
      );
      if (applicationError) {
        setError(copilotActionErrorFromApplicationError(applicationError));
      } else {
        setError({
          kind: 'generic',
          message:
            err instanceof Error ? err.message : 'Failed to upload new version. Please try again.'
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Replace Document</DialogTitle>
      <DialogDescription>
        Upload a new version of <span className="font-semibold">{documentTitle}</span>. The previous
        version will be preserved in the version history.
      </DialogDescription>
      <DialogBody>
        <FileInput onChange={handleFileChange} />
        <CopilotSubmitError className="mt-4" error={error} />
      </DialogBody>
      <DialogActions>
        <Button color="white" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button color="sky" onClick={handleReplace} loading={isUploading} disabled={!file}>
          Replace
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BulkMoveDocumentsDialog = ({
  open,
  documentIds,
  folders,
  initialFolderId,
  onClose,
  onSuccess
}: {
  open: boolean;
  documentIds: string[];
  folders: KnowledgeBaseFolderFieldsFragment[];
  initialFolderId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [destinationFolderId, setDestinationFolderId] = React.useState(
    getFolderId(initialFolderId) || ''
  );
  const [isMoving, setIsMoving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDestinationFolderId(getFolderId(initialFolderId) || '');
      setError(null);
      setIsMoving(false);
    }
  }, [open, initialFolderId]);

  const folderOptions = React.useMemo(() => buildFolderOptions(folders), [folders]);

  const handleMove = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!documentIds.length) {
      return;
    }

    setIsMoving(true);
    setError(null);

    try {
      const result = await client.mutate<MoveKnowledgeBaseDocumentsToFolderMutation>({
        mutation: MoveKnowledgeBaseDocumentsToFolderDocument,
        variables: {
          documentIds,
          folderId: destinationFolderId || null
        }
      });
      const movedDocuments = result.data?.moveKnowledgeBaseDocumentsToFolder || [];
      if (movedDocuments.length === documentIds.length) {
        onSuccess();
      } else {
        throw new Error('Failed to move selected documents');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move selected documents');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Move documents</DialogTitle>
      <DialogDescription>
        Choose a destination for {documentIds.length}{' '}
        {documentIds.length === 1 ? 'selected document' : 'selected documents'}.
      </DialogDescription>
      <Form onSubmit={handleMove}>
        <DialogBody>
          <Fieldset className="min-w-0">
            <FieldGroup className="min-w-0">
              <Field className="min-w-0">
                <Label>Destination</Label>
                <Select
                  className="min-w-0 max-w-full"
                  value={destinationFolderId}
                  onChange={(e) => setDestinationFolderId(e.currentTarget.value)}
                >
                  <option value="">All documents</option>
                  {folderOptions.map(({ folder }) => (
                    <option key={folder.id} value={folder.id}>
                      {getFolderOptionLabel(folders, folder)}
                    </option>
                  ))}
                </Select>
              </Field>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={isMoving}>
            Move
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

export const KnowledgeBaseDocumentsLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading knowledge base...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

// Track Website Dialog
const TrackWebsiteDialog = (props: {
  open: boolean;
  onClose: () => void;
  knowledgeBaseId?: string;
  folderId?: string | null;
}) => {
  const revalidator = useRevalidator();

  const [url, setUrl] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [sourceType, setSourceType] = React.useState<UrlSourceType>(UrlSourceType.Webpage);
  const [frequency, setFrequency] = React.useState<UrlMonitorFrequency>(UrlMonitorFrequency.Weekly);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<CopilotActionError>();

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!props.open) {
      setUrl('');
      setTitle('');
      setSourceType(UrlSourceType.Webpage);
      setFrequency(UrlMonitorFrequency.Weekly);
      setError(undefined);
    }
  }, [props.open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setIsSubmitting(true);

    try {
      // Validate URL
      try {
        new URL(url);
      } catch {
        throw new Error('Please enter a valid URL');
      }

      if (!title.trim()) {
        throw new Error('Please enter a title');
      }

      await client.mutate({
        mutation: CreateKnowledgeBaseUrlDocumentDocument,
        variables: {
          knowledgeBaseId: props.knowledgeBaseId || '',
          input: {
            url,
            title: title.trim(),
            folderId: props.folderId || null,
            sourceType,
            frequency
          }
        }
      });

      revalidator.revalidate();
      props.onClose();
    } catch (err) {
      const applicationError = getApplicationError(
        err instanceof ApolloError ? err : new ApolloError({ graphQLErrors: [] })
      );
      if (applicationError) {
        setError(copilotActionErrorFromApplicationError(applicationError));
      } else {
        setError({
          kind: 'generic',
          message: err instanceof Error ? err.message : 'Failed to add URL document'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog onClose={props.onClose} open={props.open} size="lg">
      <DialogTitle>Track Website</DialogTitle>
      <DialogDescription>
        Monitor a URL for changes. Cara will periodically fetch and index this content.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>URL</Label>
                <Description>
                  Enter the URL to monitor. Can be a direct download link or a webpage.
                </Description>
                <Input
                  type="url"
                  placeholder="https://example.com/document.pdf"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                />
              </Field>

              <Field>
                <Label>Title</Label>
                <Description>A descriptive title for this document.</Description>
                <Input
                  type="text"
                  placeholder="Company Appetite Guide"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Field>

              <Field>
                <Label>Source Type</Label>
                <Description>How should we capture this content?</Description>
                <Select
                  value={sourceType}
                  onChange={(e) => setSourceType(e.target.value as UrlSourceType)}
                >
                  <option value={UrlSourceType.Download}>Download file directly (PDF)</option>
                  <option value={UrlSourceType.Webpage}>Capture webpage as PDF</option>
                </Select>
              </Field>

              <Field>
                <Label>Sync Frequency</Label>
                <Description>How often should we check for updates?</Description>
                <Select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as UrlMonitorFrequency)}
                >
                  <option value={UrlMonitorFrequency.Daily}>Daily</option>
                  <option value={UrlMonitorFrequency.Weekly}>Weekly</option>
                  <option value={UrlMonitorFrequency.Monthly}>Monthly</option>
                </Select>
              </Field>

              <CopilotSubmitError className="mt-2" error={error} />
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={isSubmitting} onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" color="sky" loading={isSubmitting} disabled={!url || !title}>
            Track Website
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const KnowledgeBaseDocumentCreatePage = () => {
  const submit = useSubmit();
  const [searchParams] = useSearchParams();
  const knowledgeBaseLoader = useRouteLoaderData('knowledge/:knowledgeBaseId') as
    | { data: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }> }
    | undefined;
  const action = useActionData() as ActionResult<CreateKnowledgeBaseTextDocumentMutation>;
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [submitError, setSubmitError] = React.useState<CopilotActionError>();

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.applicationError) {
      setSubmitError(copilotActionErrorFromApplicationError(action.applicationError));
    }
  }, [action?.applicationError]);

  return (
    <React.Suspense fallback={null}>
      <Await resolve={knowledgeBaseLoader?.data}>
        {(data) => {
          const folderId = getValidFolderId(
            data?.knowledgeBase?.folders || [],
            searchParams.get('folder')
          );

          return (
            <KnowledgeBaseDocumentCreateOrEditPage
              editMode={false}
              initialForm={{
                title: '',
                content: ''
              }}
              onSubmit={(form) => {
                setSubmitError(undefined);
                submit(
                  {
                    ...form,
                    folderId: folderId || null
                  },
                  { method: 'POST', encType: 'application/json' }
                );
              }}
              validationErrors={validationErrors}
              submitError={submitError}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const KnowledgeBaseDocumentEditPage = () => {
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBaseDocument?: KnowledgeBaseDocumentDetailFieldsFragment }> }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white dark:bg-neutral-800 p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => {
          if (!data?.knowledgeBaseDocument) {
            return null;
          }

          const submit = useSubmit();
          const action = useActionData() as ActionResult<UpdateKnowledgeBaseDocumentMutation>;
          const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);

          React.useEffect(() => {
            if (action?.validationErrors) {
              setValidationErrors(action.validationErrors);
            }
          }, [action?.validationErrors]);

          return (
            <KnowledgeBaseDocumentCreateOrEditPage
              editMode
              initialForm={{
                title: data.knowledgeBaseDocument.title || '',
                content: data.knowledgeBaseDocument.text?.content || ''
              }}
              onSubmit={(form) => submit(form, { method: 'PUT', encType: 'application/json' })}
              validationErrors={validationErrors}
            />
          );
        }}
      </Await>
    </React.Suspense>
  );
};

export const KnowledgeBaseDocumentCreateOrEditPage = (props: {
  editMode?: boolean;
  initialForm: UpdateKnowledgeBaseDocumentInput;
  onSubmit: (form: UpdateKnowledgeBaseDocumentInput) => void;
  validationErrors: ValidationError[];
  submitError?: CopilotActionError;
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();

  // Reachable by URL on both routes, so the dialog checks for itself rather
  // than trusting that the menu item that normally opens it was visible.
  const canEdit = usePermissions().hasAllPermissions(
    props.editMode ? UPDATE_KB_DOCUMENT : CREATE_KB_DOCUMENT
  );

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<UpdateKnowledgeBaseDocumentInput>(props.initialForm);
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.onSubmit(form);
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="3xl" className="relative">
      <DialogTitle>
        {!canEdit ? 'View document' : props.editMode ? 'Edit document' : 'Create a new document'}
      </DialogTitle>
      <DialogDescription>
        {props.editMode
          ? 'Update the document and save your changes.'
          : 'Create a new document for Cara to reference as part of her knowledge base.'}
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          {!canEdit && (
            <PermissionNotice className="mb-6">
              You can view this document but not change it.
            </PermissionNotice>
          )}
          <Fieldset disabled={!canEdit}>
            <FieldGroup>
              <Field>
                <Label>Title</Label>
                <Description>
                  A brief, descriptive title for the document. This should be two to four words.
                </Description>
                <Input
                  className="max-w-xs"
                  value={form?.title || ''}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['title'])}
                />
                {!!getValidationError(validationErrors, ['title']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['title'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Content</Label>
                <Description>
                  The content of the document you'd like to add to Cara's knowledge base.
                </Description>
                <AutoResizeTextarea
                  className="font-mono"
                  value={form?.content || ''}
                  onChange={(e) => {
                    const content = e.currentTarget.value;
                    updateForm(['content'], (prev) => ({ ...prev, content }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['content'])}
                />
                {!!getValidationError(validationErrors, ['content']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['content'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
          <CopilotSubmitError className="mt-4" error={props.submitError} />
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          {canEdit && (
            <Button
              type="submit"
              color="sky"
              disabled={!form}
              loading={navigation.state === 'submitting'}
            >
              {props.editMode ? 'Update Document' : 'Create Document'}
            </Button>
          )}
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const KnowledgeBaseDocumentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { knowledgeBaseId } = useParams<{ knowledgeBaseId: string }>();
  const { hasAllPermissions } = usePermissions();
  const canUpdateDocument = hasAllPermissions(UPDATE_KB_DOCUMENT);
  const canDeleteDocuments = hasAllPermissions(DELETE_KB_DOCUMENT);
  const canManageBase = hasAllPermissions(MANAGE_KNOWLEDGE_BASE);
  const loader = useLoaderData() as
    | { data: Promise<{ knowledgeBaseDocument?: KnowledgeBaseDocumentFieldsFragment }> }
    | undefined;

  // Get parent route's knowledge base data to determine edit permissions
  const knowledgeBaseLoader = useRouteLoaderData('knowledge/:knowledgeBaseId') as
    | { data: Promise<{ knowledgeBase?: KnowledgeBaseDetailFieldsFragment }> }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate(`/app/knowledge/${knowledgeBaseId}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <React.Suspense
      fallback={
        <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center">
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white dark:bg-neutral-800 p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(documentData) => (
          <Await resolve={knowledgeBaseLoader?.data}>
            {(knowledgeBaseData) => {
              const invited =
                knowledgeBaseData?.knowledgeBase?.isOwner ||
                knowledgeBaseData?.knowledgeBase?.myAccess?.role ===
                  KnowledgeBaseAccessRole.Contributor;
              const canEdit = invited && canUpdateDocument;
              const canDelete = invited && canDeleteDocuments;
              const canManagePriorityContent =
                (knowledgeBaseData?.knowledgeBase?.isOwner || false) && canManageBase;

              return (
                documentData?.knowledgeBaseDocument && (
                  <DocumentPageModal
                    document={documentData.knowledgeBaseDocument}
                    knowledgeBaseId={knowledgeBaseId}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    canManagePriorityContent={canManagePriorityContent}
                    guidedRetrievalEnabled={
                      knowledgeBaseData?.knowledgeBase?.guidedRetrievalEnabled || false
                    }
                    onClose={onClose}
                  />
                )
              );
            }}
          </Await>
        )}
      </Await>
    </React.Suspense>
  );
};

// Route-based document modal (for direct links) - Split view with document preview + info panel
const DocumentPageModal = ({
  document,
  knowledgeBaseId,
  canEdit = true,
  canDelete = true,
  canManagePriorityContent = false,
  guidedRetrievalEnabled = false,
  onClose
}: {
  document: KnowledgeBaseDocumentFieldsFragment;
  knowledgeBaseId?: string;
  canEdit?: boolean;
  /** Deleting a document is a DELETE on the document, not an UPDATE. */
  canDelete?: boolean;
  canManagePriorityContent?: boolean;
  guidedRetrievalEnabled?: boolean;
  onClose: () => void;
}) => {
  const [currentDocument, setCurrentDocument] =
    React.useState<KnowledgeBaseDocumentFieldsFragment>(document);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showReplaceDialog, setShowReplaceDialog] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [isSyncing, setIsSyncing] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);
  const [showNoteDialog, setShowNoteDialog] = React.useState(false);
  const [noteValue, setNoteValue] = React.useState(document.exampleQuestions || '');
  const [contextNoteValue, setContextNoteValue] = React.useState(document.contextNote || '');
  const [isSavingNote, setIsSavingNote] = React.useState(false);
  const [noteError, setNoteError] = React.useState<string | null>(null);
  const [versionHistory, setVersionHistory] = React.useState<
    KnowledgeBaseDocumentVersionFieldsFragment[] | null
  >(null);
  const revalidator = useRevalidator();

  React.useEffect(() => {
    setCurrentDocument(document);
    setVersionHistory(null); // Reset version history when document changes
    // Reset delete/replace/sync state when document changes
    setIsDeleting(false);
    setDeleteError(null);
    setShowDeleteDialog(false);
    setShowReplaceDialog(false);
    setIsSyncing(false);
    setSyncError(null);
    setShowNoteDialog(false);
    setNoteValue(document.exampleQuestions || '');
    setContextNoteValue(document.contextNote || '');
    setIsSavingNote(false);
    setNoteError(null);
  }, [document]);

  // Load version history when modal opens
  React.useEffect(() => {
    if (!currentDocument || versionHistory !== null) return;

    const loadVersionHistory = async () => {
      try {
        const res = await client.query<GetKnowledgeBaseDocumentVersionHistoryQuery>({
          query: GetKnowledgeBaseDocumentVersionHistoryDocument,
          variables: { id: currentDocument.id }
        });
        if (res.data?.knowledgeBaseDocument?.versionHistory) {
          setVersionHistory(res.data.knowledgeBaseDocument.versionHistory);
        }
      } catch {
        setVersionHistory([]);
      }
    };

    loadVersionHistory();
  }, [currentDocument, versionHistory]);

  // Poll for indexing updates - stop if deleting
  React.useEffect(() => {
    if (currentDocument.indexingStatus.status !== IndexingStatus.Indexing || isDeleting) {
      return;
    }

    const refreshDocument = async () => {
      try {
        const res = await client.query<GetKnowledgeBaseDocumentQuery>({
          query: GetKnowledgeBaseDocumentDocument,
          variables: { id: currentDocument.id },
          fetchPolicy: 'network-only'
        });
        if (res.data?.knowledgeBaseDocument) {
          setCurrentDocument(res.data.knowledgeBaseDocument);
        }
      } catch {
        // Document may have been deleted, stop polling
      }
    };

    const interval = setInterval(refreshDocument, 3000);
    return () => clearInterval(interval);
  }, [currentDocument.indexingStatus.status, isDeleting]);

  const handleDelete = async () => {
    if (!currentDocument) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const result = await client.mutate<DeleteKnowledgeBaseDocumentMutation>({
        mutation: DeleteKnowledgeBaseDocumentDocument,
        variables: { id: currentDocument.id }
      });
      if (result.errors && result.errors.length > 0) {
        setDeleteError(result.errors.map((e) => e.message).join(', '));
        setIsDeleting(false);
        return;
      }
      // Success - close dialogs immediately, then refresh data in background
      setIsDeleting(false);
      setShowDeleteDialog(false);
      onClose();
      revalidator.revalidate();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete document');
      setIsDeleting(false);
    }
  };

  const handleSync = async () => {
    if (!currentDocument) return;
    setIsSyncing(true);
    setSyncError(null);
    try {
      const result = await client.mutate<SyncKnowledgeBaseUrlDocumentMutation>({
        mutation: SyncKnowledgeBaseUrlDocumentDocument,
        variables: { id: currentDocument.id }
      });
      if (result.errors && result.errors.length > 0) {
        setSyncError(result.errors.map((e) => e.message).join(', '));
        setIsSyncing(false);
        return;
      }
      if (result.data?.syncKnowledgeBaseURLDocument) {
        setCurrentDocument(result.data.syncKnowledgeBaseURLDocument);
      }
      setIsSyncing(false);
      revalidator.revalidate();
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to sync document');
      setIsSyncing(false);
    }
  };

  const handleSaveNote = async () => {
    if (!currentDocument) return;
    setIsSavingNote(true);
    setNoteError(null);
    try {
      const result = await client.mutate<UpdateKnowledgeBaseDocumentMutation>({
        mutation: UpdateKnowledgeBaseDocumentDocument,
        variables: {
          id: currentDocument.id,
          input: { exampleQuestions: noteValue || '', contextNote: contextNoteValue || '' }
        }
      });
      if (result.errors && result.errors.length > 0) {
        setNoteError(result.errors.map((e) => e.message).join(', '));
        setIsSavingNote(false);
        return;
      }
      if (result.data?.updateKnowledgeBaseDocument) {
        setCurrentDocument(result.data.updateKnowledgeBaseDocument);
      }
      setIsSavingNote(false);
      setShowNoteDialog(false);
      revalidator.revalidate();
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : 'Failed to save note');
      setIsSavingNote(false);
    }
  };

  const isFile = !!currentDocument.file;
  const isPdf = currentDocument.file?.mimeType === 'application/pdf';
  const isImage = currentDocument.file?.mimeType?.startsWith('image/');
  const isURLDocument = currentDocument.type === KnowledgeBaseDocumentType.Url;
  const hasMultipleVersions = versionHistory && versionHistory.length > 1;
  const displayTitle = getKnowledgeBaseDocumentDisplayTitle(currentDocument);

  return (
    <>
      <MountableDialog onClose={onClose} size="7xl">
        <div className="flex flex-col lg:flex-row -m-6">
          {/* Left side: Document Preview - Takes most of the space */}
          <div className="flex-[2] bg-neutral-100 dark:bg-neutral-700 rounded-t-2xl lg:rounded-t-none lg:rounded-l-2xl min-h-[500px] lg:min-h-[750px] flex flex-col">
            {isFile && isPdf && currentDocument.file?.url ? (
              <iframe
                src={`${currentDocument.file.url}#view=FitH&navpanes=0`}
                className="w-full h-full min-h-[500px] lg:min-h-[750px] lg:rounded-l-2xl"
                title={displayTitle}
              />
            ) : isFile && isImage && currentDocument.file?.url ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <img
                  src={currentDocument.file.url}
                  alt={displayTitle}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            ) : isFile ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <DocumentIconSolid className="h-16 w-16 text-neutral-400 dark:text-neutral-500" />
                <p className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
                  Preview not available for this file type
                </p>
                {currentDocument.file?.url && (
                  <Button
                    color="white"
                    className="mt-4"
                    href={currentDocument.file.url}
                    target="_blank"
                  >
                    <EyeIcon className="h-4 w-4 mr-1.5" />
                    Open in New Tab
                  </Button>
                )}
              </div>
            ) : (
              // Text document content
              <div className="flex-1 overflow-y-auto p-6 w-full h-full max-h-[500px] lg:max-h-[750px]">
                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm p-6 min-h-full">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    {currentDocument.text?.content}
                  </pre>
                </div>
              </div>
            )}
          </div>

          {/* Right side: Info Panel */}
          <div className="flex-1 lg:min-w-[320px] lg:max-w-[400px] p-6 flex flex-col border-t lg:border-t-0 lg:border-l border-neutral-200 dark:border-neutral-700 max-h-[500px] lg:max-h-[750px]">
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Header */}
              <div className="mb-4">
                <h2 className="font-semibold text-neutral-900 dark:text-white mt-0 line-clamp-2">
                  {displayTitle}
                </h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate mt-1">
                  {isFile ? currentDocument.file?.name : 'Text document'}
                </p>
              </div>

              {/* Summary */}
              {currentDocument.metadata?.summary && (
                <div className="mb-6 rounded-lg">
                  <h4 className="text-sm font-medium font-body text-neutral-500 dark:text-neutral-400 tracking-wide mt-0 mb-2">
                    Summary
                  </h4>
                  <p className="text-sm text-neutral-900 dark:text-white line-clamp-4">
                    {currentDocument.metadata.summary}
                  </p>
                </div>
              )}

              {/* Keywords */}
              {currentDocument.metadata?.keywords &&
                currentDocument.metadata.keywords.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium font-body text-neutral-500 dark:text-neutral-400 tracking-wide mt-0 mb-2">
                      Keywords
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {currentDocument.metadata.keywords.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center rounded-full bg-sky-50 dark:bg-sky-500/10 border border-sky-100 dark:border-sky-500/20 px-2 py-0.5 text-xs font-medium text-sky-700 dark:text-sky-300"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {/* Properties - Compact */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col">
                  <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                    Type
                  </span>
                  <span className="text-neutral-900 dark:text-white">
                    {currentDocument.type === KnowledgeBaseDocumentType.Url
                      ? 'URL'
                      : isFile
                        ? 'File'
                        : 'Text'}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                    Created
                  </span>
                  <span className="text-neutral-900 dark:text-white">
                    {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                      new Date(currentDocument.createdAt)
                    )}
                  </span>
                </div>
                {currentDocument.createdBy?.name && (
                  <div className="flex flex-col">
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                      Uploaded By
                    </span>
                    <span className="text-neutral-900 dark:text-white truncate">
                      {currentDocument.createdBy.name}
                    </span>
                  </div>
                )}
                {isFile && currentDocument.file && (
                  <div className="flex flex-col">
                    <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                      Size
                    </span>
                    <span className="text-neutral-900 dark:text-white">
                      {formatFileSize(currentDocument.file.size)}
                    </span>
                  </div>
                )}
                {isURLDocument && currentDocument.urlSource && (
                  <>
                    <div className="flex flex-col">
                      <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                        Sync Frequency
                      </span>
                      <span className="text-neutral-900 dark:text-white">
                        {currentDocument.urlSource.frequency === UrlMonitorFrequency.Daily
                          ? 'Daily'
                          : currentDocument.urlSource.frequency === UrlMonitorFrequency.Weekly
                            ? 'Weekly'
                            : 'Monthly'}
                        {currentDocument.urlSource.isPaused && (
                          <Badge color="amber" className="ml-2">
                            Paused
                          </Badge>
                        )}
                      </span>
                    </div>
                    {currentDocument.urlLastSyncedAt && (
                      <div className="flex flex-col">
                        <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                          Last Synced
                        </span>
                        <span className="text-neutral-900 dark:text-white">
                          {new Intl.DateTimeFormat('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }).format(new Date(currentDocument.urlLastSyncedAt))}
                        </span>
                      </div>
                    )}
                    {currentDocument.urlLastSyncError && (
                      <div className="flex flex-col col-span-2">
                        <span className="text-neutral-500 dark:text-neutral-400 font-medium mb-1">
                          Sync Error
                        </span>
                        <span className="text-red-600 dark:text-red-400 text-xs">
                          {currentDocument.urlLastSyncError}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Indexing Progress */}
              {currentDocument.indexingStatus.status === IndexingStatus.Indexing && (
                <div className="mt-4 rounded-lg border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 p-3">
                  <div className="flex items-center gap-2">
                    <Spinner size="size-4" color="neutral" />
                    <span className="text-sm font-medium text-sky-700 dark:text-sky-300">
                      Indexing...
                    </span>
                  </div>
                  {currentDocument.indexingStatus.progress !== null && (
                    <div className="mt-2">
                      <div className="h-1.5 w-full rounded-full bg-sky-200 dark:bg-sky-500/25">
                        <div
                          className="h-1.5 rounded-full bg-sky-600 transition-all"
                          style={{
                            width: `${(currentDocument.indexingStatus.progress || 0) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {currentDocument.indexingStatus.status === IndexingStatus.Failed &&
                currentDocument.indexingStatus.error && (
                  <div className="mt-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300">
                    <h4 className="font-medium font-body mt-0">Failed</h4>
                    <p className="mt-1 text-red-600 dark:text-red-400">
                      {currentDocument.indexingStatus.error}
                    </p>
                  </div>
                )}

              {/* Version History Timeline */}
              <AnimatePresence>
                {hasMultipleVersions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 overflow-hidden"
                  >
                    <h4 className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <ClockIcon className="h-4 w-4" />
                      Version History
                    </h4>
                    <div className="flow-root">
                      <ul className="-mb-4">
                        {versionHistory.map((version, idx) => (
                          <li key={version.id}>
                            <div className="relative pb-4">
                              {/* Timeline connector line */}
                              {idx !== versionHistory.length - 1 && (
                                <span className="absolute left-3 top-6 -ml-px h-full w-0.5 bg-neutral-200 dark:bg-neutral-700" />
                              )}
                              <div className="relative flex items-start gap-3">
                                {/* Timeline dot - first item is latest (sorted by version desc) */}
                                <div
                                  className={clsx(
                                    'flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white dark:ring-neutral-800',
                                    idx === 0 ? 'bg-sky-500' : 'bg-neutral-300 dark:bg-neutral-600'
                                  )}
                                >
                                  <span className="text-[10px] font-bold text-white">
                                    {version.versionNumber}
                                  </span>
                                </div>
                                {/* Version info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    {idx === 0 && <Badge color="sky">Current</Badge>}
                                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                      {new Intl.DateTimeFormat('en-US', {
                                        dateStyle: 'medium',
                                        timeStyle: 'short'
                                      }).format(new Date(version.createdAt))}
                                    </span>
                                  </div>
                                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                                    by {version.createdBy?.name || 'Unknown'}
                                  </p>
                                </div>
                                {/* View button */}
                                {version.file?.url && (
                                  <Button
                                    color="white"
                                    href={version.file.url}
                                    target="_blank"
                                    className="text-xs px-2 py-1"
                                  >
                                    View
                                  </Button>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Priority Content */}
              {canManagePriorityContent && guidedRetrievalEnabled && (
                <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <button
                    type="button"
                    className="text-xs text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-400"
                    onClick={() => {
                      setNoteValue(currentDocument.exampleQuestions || '');
                      setContextNoteValue(currentDocument.contextNote || '');
                      setNoteError(null);
                      setShowNoteDialog(true);
                    }}
                  >
                    {currentDocument.exampleQuestions || currentDocument.contextNote
                      ? 'Edit priority content'
                      : 'Add priority content'}
                  </button>
                </div>
              )}
            </div>

            {/* Actions - Fixed at bottom */}
            <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700 flex flex-col gap-2 flex-shrink-0">
              {!isFile && (
                <Button
                  color="sky"
                  className="w-full justify-center"
                  href={`/app/knowledge/${knowledgeBaseId}/${currentDocument.id}/edit`}
                >
                  Edit
                </Button>
              )}
              {isFile && currentDocument.file?.url && (
                <Button
                  color="sky"
                  className="w-full justify-center"
                  href={currentDocument.file.url}
                  target="_blank"
                >
                  Open
                </Button>
              )}
              {syncError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">{syncError}</p>
              )}
              <div className="flex gap-2">
                {canDelete && (
                  <Button
                    color="white"
                    className="flex-1 justify-center"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    Delete
                  </Button>
                )}
                {canEdit && (
                  <>
                    {isURLDocument ? (
                      <Button
                        color="white"
                        className="flex-1 justify-center"
                        onClick={handleSync}
                        loading={isSyncing}
                      >
                        Sync Now
                      </Button>
                    ) : (
                      <Button
                        color="white"
                        className="flex-1 justify-center"
                        onClick={() => setShowReplaceDialog(true)}
                      >
                        Replace
                      </Button>
                    )}
                  </>
                )}
                <Button color="white" className="flex-1 justify-center" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </MountableDialog>

      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} size="md">
        <DialogTitle>Delete Document</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete <span className="font-semibold">{displayTitle}</span>?
          This action is permanent.
        </DialogDescription>
        {deleteError && (
          <DialogBody>
            <p className="text-sm text-red-600 dark:text-red-400">{deleteError}</p>
          </DialogBody>
        )}
        <DialogActions>
          <Button color="white" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete} loading={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ReplaceDocumentDialog
        open={showReplaceDialog}
        documentId={currentDocument.id}
        documentTitle={displayTitle}
        onClose={() => setShowReplaceDialog(false)}
        onSuccess={() => revalidator.revalidate()}
      />

      <Dialog open={showNoteDialog} onClose={() => setShowNoteDialog(false)} size="md">
        <DialogTitle>Priority Content</DialogTitle>
        <DialogDescription>Tell Cara when to force this document into context.</DialogDescription>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveNote();
          }}
        >
          <DialogBody>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Example questions
                </label>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  User questions or scenarios that should pull in the full document.
                </p>
                <Textarea
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value.slice(0, 500))}
                  rows={5}
                  placeholder="e.g. What are Travelers commercial auto filing guidelines in California?"
                />
                <p className="text-xs text-neutral-400 dark:text-neutral-500 text-right">
                  {noteValue.length}/500
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Context
                </label>
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  How should Cara use this document when it is included?
                </p>
                <Textarea
                  value={contextNoteValue}
                  onChange={(e) => setContextNoteValue(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder="e.g. Use this document as the primary source for carrier-specific vehicle conversion rules."
                />
                <p className="text-xs text-neutral-400 dark:text-neutral-500 text-right">
                  {contextNoteValue.length}/500
                </p>
              </div>
              {noteError && <p className="text-sm text-red-600 dark:text-red-400">{noteError}</p>}
            </div>
          </DialogBody>
          <DialogActions>
            <Button
              type="button"
              color="white"
              onClick={() => setShowNoteDialog(false)}
              disabled={isSavingNote}
            >
              Cancel
            </Button>
            <Button type="submit" color="sky" loading={isSavingNote}>
              Save
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

// =============================================================================
// Knowledge Base Settings Dialog
// =============================================================================

interface KnowledgeBaseSettingsDialogProps {
  open: boolean;
  knowledgeBaseId: string;
  knowledgeBaseTitle: string;
  guidedRetrievalEnabled: boolean;
  accessList: KnowledgeBaseAccessFieldsFragment[];
  onClose: () => void;
  onUpdate: () => void;
}

const KnowledgeBaseSettingsDialog = ({
  open,
  knowledgeBaseId,
  knowledgeBaseTitle,
  guidedRetrievalEnabled,
  accessList,
  onClose,
  onUpdate
}: KnowledgeBaseSettingsDialogProps) => {
  const [childAgencies, setChildAgencies] = React.useState<{ id: string; name: string }[] | null>(
    null
  );
  const [loadingAgencies, setLoadingAgencies] = React.useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = React.useState<string>('');
  const [selectedRole, setSelectedRole] = React.useState<KnowledgeBaseAccessRole>(
    KnowledgeBaseAccessRole.Viewer
  );
  const [isGranting, setIsGranting] = React.useState(false);
  const [updatingAgencyId, setUpdatingAgencyId] = React.useState<string | null>(null);
  const [revokingAgencyId, setRevokingAgencyId] = React.useState<string | null>(null);
  const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);

  // Load federated child agencies when dialog opens
  React.useEffect(() => {
    if (open && childAgencies === null) {
      setLoadingAgencies(true);
      client
        .query<GetFederatedChildAgenciesQuery>({
          query: GetFederatedChildAgenciesDocument
        })
        .then((res) => {
          setChildAgencies(res.data.federatedChildAgencies || []);
        })
        .catch(() => {
          setChildAgencies([]);
        })
        .finally(() => {
          setLoadingAgencies(false);
        });
    }
  }, [open, childAgencies]);

  // Filter out agencies that already have access
  const availableAgencies = React.useMemo(() => {
    if (!childAgencies) return [];
    const existingIds = new Set(accessList.map((a) => a.agency.id));
    return childAgencies.filter((a) => !existingIds.has(a.id));
  }, [childAgencies, accessList]);

  const handleGrantAccess = async () => {
    if (!selectedAgencyId) return;
    setIsGranting(true);
    try {
      await client.mutate<GrantKnowledgeBaseAccessMutation>({
        mutation: GrantKnowledgeBaseAccessDocument,
        variables: {
          knowledgeBaseId,
          agencyId: selectedAgencyId,
          role: selectedRole
        }
      });
      setSelectedAgencyId('');
      setSelectedRole(KnowledgeBaseAccessRole.Viewer);
      onUpdate();
    } catch {
      // Error handling - access grant failed
    } finally {
      setIsGranting(false);
    }
  };

  const handleUpdateRole = async (agencyId: string, newRole: KnowledgeBaseAccessRole) => {
    setUpdatingAgencyId(agencyId);
    try {
      await client.mutate<UpdateKnowledgeBaseAccessMutation>({
        mutation: UpdateKnowledgeBaseAccessDocument,
        variables: {
          knowledgeBaseId,
          agencyId,
          role: newRole
        }
      });
      onUpdate();
    } catch {
      // Error handling - access update failed
    } finally {
      setUpdatingAgencyId(null);
    }
  };

  const handleRevokeAccess = async (agencyId: string) => {
    setRevokingAgencyId(agencyId);
    try {
      await client.mutate<RevokeKnowledgeBaseAccessMutation>({
        mutation: RevokeKnowledgeBaseAccessDocument,
        variables: {
          knowledgeBaseId,
          agencyId
        }
      });
      onUpdate();
    } catch {
      // Error handling - access revoke failed
    } finally {
      setRevokingAgencyId(null);
    }
  };

  const handleGuidedRetrievalToggle = async (enabled: boolean) => {
    setIsUpdatingSettings(true);
    try {
      await client.mutate<UpdateKnowledgeBaseMutation>({
        mutation: UpdateKnowledgeBaseMutationDocument,
        variables: {
          id: knowledgeBaseId,
          input: { guidedRetrievalEnabled: enabled }
        }
      });
      onUpdate();
    } catch {
      // Error handling - settings update failed
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  const hasNoChildAgencies = !loadingAgencies && childAgencies && childAgencies.length === 0;

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogTitle>Knowledge Base Settings</DialogTitle>
      <DialogDescription>
        Manage sharing settings for <span className="font-semibold">{knowledgeBaseTitle}</span>
      </DialogDescription>

      <DialogBody>
        <div className="mb-6 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-neutral-900 dark:text-white">
                Priority content
              </p>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                Allow documents to define example questions that force full-document context when
                matched.
              </p>
            </div>
            <Checkbox
              aria-label="Enable priority content"
              checked={guidedRetrievalEnabled}
              color="sky"
              disabled={isUpdatingSettings}
              onChange={handleGuidedRetrievalToggle}
            />
          </div>
        </div>

        {/* Sharing Section */}
        {hasNoChildAgencies ? (
          <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900 p-8 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
            <p className="mt-4 text-sm font-medium text-neutral-900 dark:text-white">
              No sub-agencies available
            </p>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              You don't have any sub-agencies with federated billing to share with.
            </p>
          </div>
        ) : (
          <>
            {/* Add new access */}
            {availableAgencies.length > 0 && (
              <div className="flex gap-3 mb-6">
                <div className="flex-1">
                  <Select
                    value={selectedAgencyId}
                    onChange={(e) => setSelectedAgencyId(e.target.value)}
                  >
                    <option value="">Select an agency...</option>
                    {availableAgencies.map((agency) => (
                      <option key={agency.id} value={agency.id}>
                        {agency.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="w-36">
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as KnowledgeBaseAccessRole)}
                  >
                    <option value={KnowledgeBaseAccessRole.Viewer}>Viewer</option>
                    <option value={KnowledgeBaseAccessRole.Contributor}>Contributor</option>
                  </Select>
                </div>

                <Button
                  color="sky"
                  onClick={handleGrantAccess}
                  disabled={!selectedAgencyId || isGranting}
                  loading={isGranting}
                >
                  Share
                </Button>
              </div>
            )}

            {/* Current access list */}
            {accessList.length > 0 ? (
              <div className="space-y-3">
                {accessList.map((access) => (
                  <div
                    key={access.agency.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-neutral-300 dark:hover:border-white/20 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                        {access.agency.name}
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        Shared{' '}
                        {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                          new Date(access.createdAt)
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <div className="relative w-32">
                        <Select
                          value={access.role}
                          onChange={(e) =>
                            handleUpdateRole(
                              access.agency.id,
                              e.target.value as KnowledgeBaseAccessRole
                            )
                          }
                          disabled={updatingAgencyId === access.agency.id}
                        >
                          <option value={KnowledgeBaseAccessRole.Viewer}>Viewer</option>
                          <option value={KnowledgeBaseAccessRole.Contributor}>Contributor</option>
                        </Select>
                        {updatingAgencyId === access.agency.id && (
                          <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
                            <Spinner size="size-3.5" color="neutral" />
                          </div>
                        )}
                      </div>

                      <Button
                        color="white"
                        onClick={() => handleRevokeAccess(access.agency.id)}
                        loading={revokingAgencyId === access.agency.id}
                        title="Remove access"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              availableAgencies.length === 0 && (
                <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900 p-8 text-center">
                  <ShareIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                  <p className="mt-4 text-sm font-medium text-neutral-900 dark:text-white">
                    All sub-agencies have access
                  </p>
                  <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                    This knowledge base is shared with all available sub-agencies.
                  </p>
                </div>
              )
            )}

            {accessList.length === 0 && availableAgencies.length > 0 && (
              <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900 p-8 text-center">
                <ShareIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                <p className="mt-4 text-sm font-medium text-neutral-900 dark:text-white">
                  Not shared yet
                </p>
                <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
                  Select an agency above to share access to this knowledge base.
                </p>
              </div>
            )}
          </>
        )}
      </DialogBody>

      <DialogActions>
        <Button color="white" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
